import { z } from 'zod';
/**
 * Zod-схемы для трёх личных справочников клиента (Этап 3).
 *
 * Видимы только владельцу: API проверяет, что `clientId` записи совпадает
 * с `clientId` текущего пользователя.
 *
 * Soft-delete: записи никогда не удаляются физически, только `archivedAt`.
 */
// =====================================================================
// AddressBookEntry — «Мои адреса»
// =====================================================================
export const addressBookCreateSchema = z.object({
    /** Короткое название, чтобы узнать запись в списках. */
    label: z.string().min(1).max(200),
    /** Страна (опц.) — отдельно от строки адреса. */
    country: z.string().max(100).optional(),
    /** Полный адрес (без страны — она отдельным полем). */
    address: z.string().min(1).max(500),
    contactName: z.string().max(200).optional(),
    contactPhone: z.string().max(50).optional(),
    /** Тип погрузки: «задняя», «боковая», «верхняя» — для подбора транспорта. */
    loadingType: z.string().max(100).optional(),
});
export const addressBookPatchSchema = addressBookCreateSchema.partial();
export const addressBookEntrySchema = z.object({
    id: z.string(),
    clientId: z.string(),
    label: z.string(),
    country: z.string().nullable(),
    address: z.string(),
    contactName: z.string().nullable(),
    contactPhone: z.string().nullable(),
    loadingType: z.string().nullable(),
    createdAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
});
// =====================================================================
// CargoTemplate — «Мои грузы»
// =====================================================================
export const cargoTemplateCreateSchema = z.object({
    /** Наименование груза «для себя». */
    label: z.string().min(1).max(200),
    /** Описание груза «для экспедитора и документов». */
    name: z.string().min(1).max(200),
    /** Категория — опц., скрыта в UI на текущем этапе. */
    category: z.string().max(100).optional(),
    packaging: z.string().max(200).optional(),
    /** Вес единицы товара (кг). */
    defaultWeightKg: z.number().nonnegative().optional(),
    /** Объём единицы товара (м³). */
    defaultVolumeM3: z.number().nonnegative().optional(),
    /** Код ТН ВЭД. */
    tnVedCode: z.string().max(20).optional(),
    /** Код ГНГ (Гармонизированная номенклатура грузов). */
    gngCode: z.string().max(20).optional(),
    /** Код ЕТ СНГ (Единая тарифная статистическая номенклатура СНГ). */
    etSngCode: z.string().max(20).optional(),
    isDangerous: z.boolean().default(false),
    /** Класс опасности по ДОПОГ — обязателен, если `isDangerous=true`. */
    adrClass: z.string().max(20).optional(),
});
export const cargoTemplatePatchSchema = cargoTemplateCreateSchema.partial();
export const cargoTemplateSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    label: z.string(),
    name: z.string(),
    category: z.string().nullable(),
    packaging: z.string().nullable(),
    defaultWeightKg: z.number().nullable(),
    defaultVolumeM3: z.number().nullable(),
    tnVedCode: z.string().nullable(),
    gngCode: z.string().nullable(),
    etSngCode: z.string().nullable(),
    isDangerous: z.boolean(),
    adrClass: z.string().nullable(),
    createdAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
});
// =====================================================================
// ClientCounterparty — «Мои контрагенты»
// =====================================================================
/** Тип лица контрагента (для электронного титула ИдСв). Совпадает с ClientType. */
export const counterpartyPartyTypeSchema = z.enum([
    'LEGAL_RU',
    'ENTREPRENEUR',
    'INDIVIDUAL',
    'LEGAL_FOREIGN',
]);
export const counterpartyCreateSchema = z.object({
    label: z.string().min(1).max(200),
    name: z.string().min(1).max(500),
    partyType: counterpartyPartyTypeSchema.optional(),
    inn: z.string().max(20).optional(),
    kpp: z.string().max(20).optional(),
    ogrn: z.string().max(20).optional(),
    country: z.string().max(100).optional(),
    address: z.string().max(500).optional(),
    contactName: z.string().max(200).optional(),
    contactPhone: z.string().max(50).optional(),
});
export const counterpartyPatchSchema = counterpartyCreateSchema.partial();
export const counterpartySchema = z.object({
    id: z.string(),
    clientId: z.string(),
    label: z.string(),
    name: z.string(),
    partyType: counterpartyPartyTypeSchema.nullable(),
    inn: z.string().nullable(),
    kpp: z.string().nullable(),
    ogrn: z.string().nullable(),
    country: z.string().nullable(),
    address: z.string().nullable(),
    contactName: z.string().nullable(),
    contactPhone: z.string().nullable(),
    createdAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
});
// =====================================================================
// Общие параметры списков
// =====================================================================
/** Включать ли архивные записи в выдачу. По умолчанию — нет. */
export const listReferenceBookQuerySchema = z.object({
    includeArchived: z
        .union([z.boolean(), z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => v === true || v === 'true'),
});
//# sourceMappingURL=reference-books.js.map